import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { firestore } from '../../shared/firebase/config';
import { ShieldCheck, CheckCircle2, FileText, Lock, Award, Users } from 'lucide-react';

const formatContent = (raw: string) => {
 if (!raw) return '';
 if (/<[a-z][\s\S]*>/i.test(raw)) {
 return raw; // Already HTML
 }
 const lines = raw.split('\n').filter(Boolean);
 let html = '';
 let inList = false;

 lines.forEach(line => {
 const trimmed = line.trim();
 if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
 if (!inList) {
 html += '<ul style="padding-left: 20px; margin-bottom: 14px; display: flex; flex-direction: column; gap: 6px; color: var(--text-secondary);">';
 inList = true;
 }
 html += `<li>${trimmed.replace(/^[•\-]\s*/, '')}</li>`;
 } else {
 if (inList) {
 html += '</ul>';
 inList = false;
 }
 if (/^\d+\./.test(trimmed)) {
 html += `<h3 style="font-size: 15px; font-weight: 700; margin: 16px 0 8px 0; color: var(--text-primary);">${trimmed}</h3>`;
 } else {
 html += `<p style="margin-bottom: 10px; color: var(--text-secondary);">${trimmed}</p>`;
 }
 }
 });

 if (inList) html += '</ul>';
 return html;
};

interface PolicyUpdateModalProps {
 uid: string;
 userDoc: any;
}

type TabKey = 'terms' | 'privacy' | 'refund' | 'placement' | 'referrer';

export const PolicyUpdateModal: React.FC<PolicyUpdateModalProps> = ({ uid, userDoc }) => {
 const [legalData, setLegalData] = useState<any>(null);
 const [isAccepting, setIsAccepting] = useState(false);
 const [showModal, setShowModal] = useState(false);
 const [activeTab, setActiveTab] = useState<TabKey>('terms');

 useEffect(() => {
 if (!uid || !userDoc) return;

 const unsub = onSnapshot(doc(firestore, 'platformSettings', 'legal'), (snap) => {
 if (snap.exists()) {
 const data = snap.data();
 setLegalData(data);
 const activeVersion = data.version || 'v1.0';
 const userAcceptedVersion = userDoc.acceptedLegalVersion;

 // If user hasn't accepted current published version, show modal
 if (!userAcceptedVersion || userAcceptedVersion !== activeVersion) {
 setShowModal(true);
 } else {
 setShowModal(false);
 }
 }
 });

 return unsub;
 }, [uid, userDoc]);

 if (!showModal || !legalData) return null;

 const handleAccept = async () => {
 setIsAccepting(true);
 try {
 const activeVersion = legalData.version || 'v1.0';
 const userRef = doc(firestore, 'users', uid);
 await updateDoc(userRef, {
 acceptedLegalVersion: activeVersion,
 termsAcceptedAt: serverTimestamp(),
 updatedAt: serverTimestamp()
 });

 // Write audit log
 await addDoc(collection(firestore, 'auditLogs'), {
 action: 'POLICY_REACCEPTANCE',
 details: `User accepted updated platform legal policies version ${activeVersion}`,
 performedBy: userDoc.email || uid,
 performedByUid: uid,
 role: userDoc.role || 'user',
 createdAt: new Date()
 });

 setShowModal(false);
 } catch (err: any) {
 console.error('Error accepting updated policies:', err);
 } finally {
 setIsAccepting(false);
 }
 };

 const getContent = () => {
 switch (activeTab) {
 case 'terms': return legalData.termsOfService || '';
 case 'privacy': return legalData.privacyPolicy || '';
 case 'refund': return legalData.refundDepositPolicy || '';
 case 'placement': return legalData.placementGuaranteePolicy || '';
 case 'referrer': return legalData.referrerCommissionPolicy || '';
 default: return legalData.termsOfService || '';
 }
 };

 return (
 <div
 className="policyUpdateOverlay"
 style={{
 position: 'fixed',
 top: 0,
 left: 0,
 right: 0,
 bottom: 0,
 backgroundColor: 'rgba(0, 0, 0, 0.85)',
 backdropFilter: 'blur(10px)',
 zIndex: 999999,
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 padding: '16px'
 }}
 >
 <style>{`
 @media (max-width: 640px) {
 .policyUpdateOverlay {
 padding: 8px !important;
 }
 .policyUpdateCard {
 max-height: 94vh !important;
 border-radius: 12px !important;
 }
 .policyUpdateHeader {
 padding: 16px 16px 12px 16px !important;
 }
 .policyUpdateHeader h2 {
 font-size: 15px !important;
 }
 .policyUpdateTabs {
 padding: 8px 12px !important;
 gap: 4px !important;
 }
 .policyUpdateTabBtn {
 padding: 6px 10px !important;
 font-size: 11px !important;
 }
 .policyUpdateBody {
 padding: 14px 16px !important;
 font-size: 12px !important;
 }
 .policyUpdateFooter {
 padding: 12px 16px !important;
 }
 .policyUpdateAcceptBtn {
 padding: 12px !important;
 font-size: 13px !important;
 }
 }
 `}</style>
 <div
 className="policyUpdateCard"
 style={{
 backgroundColor: 'var(--bg-surface, #161c2b)',
 border: '1px solid var(--border-strong, rgba(255, 255, 255, 0.2))',
 borderRadius: '16px',
 width: '100%',
 maxWidth: '680px',
 maxHeight: '85vh',
 display: 'flex',
 flexDirection: 'column',
 boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
 color: 'var(--text-primary, #fff)',
 overflow: 'hidden'
 }}
 >
 {/* Header */}
 <div className="policyUpdateHeader" style={{ padding: '24px 28px 16px 28px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
 <div style={{ padding: '12px', borderRadius: '12px', background: 'rgba(37, 99, 235, 0.15)', color: '#60a5fa', flexShrink: 0 }}>
 <ShieldCheck size={28} />
 </div>
 <div>
 <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
 Updated Platform Legal Policies ({legalData.version || 'v1.0'})
 </h2>
 <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
 JobnVisa Legal & Governance Compliance Update
 </span>
 </div>
 </div>

 {legalData.changesSummary && (
 <div style={{ marginTop: '12px', padding: '10px 14px', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '8px', fontSize: '12px', color: '#fbbf24' }}>
 <strong>What Changed:</strong> {legalData.changesSummary}
 </div>
 )}
 </div>

 {/* Tab Buttons */}
 <div
 className="policyUpdateTabs"
 style={{
 display: 'flex',
 gap: '6px',
 padding: '10px 20px',
 borderBottom: '1px solid var(--border-subtle)',
 overflowX: 'auto',
 WebkitOverflowScrolling: 'touch',
 background: 'var(--bg-surface)'
 }}
 >
 <button
 className="policyUpdateTabBtn"
 onClick={() => setActiveTab('terms')}
 style={{
 padding: '6px 12px',
 borderRadius: '6px',
 fontSize: '12px',
 fontWeight: 600,
 border: 'none',
 cursor: 'pointer',
 display: 'flex',
 alignItems: 'center',
 gap: '6px',
 whiteSpace: 'nowrap',
 background: activeTab === 'terms' ? 'var(--color-brand-500)' : 'transparent',
 color: activeTab === 'terms' ? '#fff' : 'var(--text-muted)'
 }}
 >
 <FileText size={13} /> Terms of Service
 </button>
 <button
 className="policyUpdateTabBtn"
 onClick={() => setActiveTab('privacy')}
 style={{
 padding: '6px 12px',
 borderRadius: '6px',
 fontSize: '12px',
 fontWeight: 600,
 border: 'none',
 cursor: 'pointer',
 display: 'flex',
 alignItems: 'center',
 gap: '6px',
 whiteSpace: 'nowrap',
 background: activeTab === 'privacy' ? 'var(--color-brand-500)' : 'transparent',
 color: activeTab === 'privacy' ? '#fff' : 'var(--text-muted)'
 }}
 >
 <Lock size={13} /> Privacy Policy
 </button>
 <button
 className="policyUpdateTabBtn"
 onClick={() => setActiveTab('refund')}
 style={{
 padding: '6px 12px',
 borderRadius: '6px',
 fontSize: '12px',
 fontWeight: 600,
 border: 'none',
 cursor: 'pointer',
 display: 'flex',
 alignItems: 'center',
 gap: '6px',
 whiteSpace: 'nowrap',
 background: activeTab === 'refund' ? 'var(--color-brand-500)' : 'transparent',
 color: activeTab === 'refund' ? '#fff' : 'var(--text-muted)'
 }}
 >
 <ShieldCheck size={13} /> Refunds & Deposits
 </button>
 <button
 className="policyUpdateTabBtn"
 onClick={() => setActiveTab('placement')}
 style={{
 padding: '6px 12px',
 borderRadius: '6px',
 fontSize: '12px',
 fontWeight: 600,
 border: 'none',
 cursor: 'pointer',
 display: 'flex',
 alignItems: 'center',
 gap: '6px',
 whiteSpace: 'nowrap',
 background: activeTab === 'placement' ? 'var(--color-brand-500)' : 'transparent',
 color: activeTab === 'placement' ? '#fff' : 'var(--text-muted)'
 }}
 >
 <Award size={13} /> Placement Guarantee
 </button>
 <button
 className="policyUpdateTabBtn"
 onClick={() => setActiveTab('referrer')}
 style={{
 padding: '6px 12px',
 borderRadius: '6px',
 fontSize: '12px',
 fontWeight: 600,
 border: 'none',
 cursor: 'pointer',
 display: 'flex',
 alignItems: 'center',
 gap: '6px',
 whiteSpace: 'nowrap',
 background: activeTab === 'referrer' ? 'var(--color-brand-500)' : 'transparent',
 color: activeTab === 'referrer' ? '#fff' : 'var(--text-muted)'
 }}
 >
 <Users size={13} /> Affiliate Terms
 </button>
 </div>

 {/* Formatted Policy Content */}
 <div
 className="policyUpdateBody"
 style={{
 padding: '20px 28px',
 overflowY: 'auto',
 flex: 1,
 fontSize: '13px',
 lineHeight: 1.6,
 color: 'var(--text-secondary)',
 background: 'var(--bg-base)'
 }}
 dangerouslySetInnerHTML={{ __html: formatContent(getContent()) }}
 />

 {/* Footer Accept Action */}
 <div className="policyUpdateFooter" style={{ padding: '16px 28px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)' }}>
 <button
 className="policyUpdateAcceptBtn"
 onClick={handleAccept}
 disabled={isAccepting}
 style={{
 width: '100%',
 padding: '14px',
 borderRadius: '10px',
 background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
 color: '#fff',
 border: 'none',
 fontSize: '14px',
 fontWeight: 700,
 cursor: isAccepting ? 'not-allowed' : 'pointer',
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 gap: '8px'
 }}
 >
 <CheckCircle2 size={18} />
 {isAccepting ? 'Accepting & Updating...' : `I Accept Updated Terms (${legalData.version || 'v1.0'})`}
 </button>
 </div>
 </div>
 </div>
 );
};
